import React from "react";
import { toast } from "sonner";
import { Coins, Ticket, Gift, ArrowDownRight, ArrowUpRight, Check, Sparkles, Trophy, Star, Lock, PartyPopper } from "lucide-react";
import { REWARDS } from "../data/destinations";
import { CHALLENGES, challengeProgress } from "../data/challenges";
import { HOLIDAYS, holidayProgress, activeHolidayId } from "../data/holidays";
import { useApp } from "../context/AppState";
import { rupiah } from "../lib/format";

function timeAgo(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function Hadiah() {
  const { state, redeemReward, claimChallenge, claimHoliday } = useApp();
  if (!state) return null;
  const owned = state.vouchers || [];
  const activeVouchers = owned.filter((v) => !v.used);
  const activeHoliday = activeHolidayId();
  const holidayDone = holidayProgress(state);

  const redeem = (r) => {
    if (state.points < r.cost) return toast.error("Poin belum cukup untuk voucher ini");
    if (redeemReward(r)) toast.success(`Berhasil tukar ${r.label} 🎉`);
  };

  const claim = (c) => {
    if (claimChallenge(c)) toast.success(`+${c.reward} poin! Tantangan selesai 🎉`);
  };

  const claimH = (h) => {
    if (claimHoliday(h)) toast.success(`Aksesori ${h.reward.label} ${h.reward.emoji} terbuka! +${h.bonus} poin 🎉`);
    else toast.error("Selesaikan misi dulu untuk klaim hadiah");
  };

  return (
    <div className="kk-reveal">
      {/* balance banner */}
      <section className="relative overflow-hidden rounded-3xl kk-coral-gradient p-6 text-white sm:p-8" data-testid="balance-banner">
        <div className="absolute -right-8 -top-10 h-44 w-44 rounded-full bg-white/10" />
        <div className="absolute -bottom-12 right-16 h-32 w-32 rounded-full bg-white/10" />
        <div className="relative">
          <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-widest text-white/85">
            <Coins className="h-4 w-4" /> Saldo poin kalian
          </div>
          <div className="mt-2 flex items-end gap-2">
            <span className="text-5xl font-extrabold text-white sm:text-6xl">{Number(state.points).toLocaleString("id-ID")}</span>
            <span className="mb-2 text-[15px] font-semibold text-white/85">poin kencan</span>
          </div>
          <p className="mt-2 max-w-md text-[14px] text-white/85">
            Tukarkan poin jadi diskon untuk kencan berikutnya. 1 poin didapat dari tiap Rp1.000 kencan yang selesai.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/20 px-3 py-1.5 text-[13px] font-bold text-white backdrop-blur">
            <Ticket className="h-4 w-4" /> {activeVouchers.length} voucher aktif
          </div>
        </div>
      </section>

      {/* Tantangan berdua */}
      <div className="mb-4 mt-8 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
          <Trophy className="h-6 w-6 text-[#e0a94b]" /> Tantangan berdua
        </h2>
        <span className="text-[12px] font-semibold text-[#8a8278]">Reset tiap minggu</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {CHALLENGES.map((c) => {
          const prog = challengeProgress(state.challenges, c);
          const done = prog >= c.target;
          const claimed = (state.challenges?.claimed || []).includes(c.id);
          const pct = Math.min(100, (prog / c.target) * 100);
          return (
            <div key={c.id} data-testid={`challenge-${c.id}`}
              className={`flex flex-col rounded-3xl border bg-white p-5 ${done && !claimed ? "border-[#f0d3da]" : "border-[#efeae4]"}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="text-[15px] font-bold leading-tight">{c.label}</span>
                <span className="flex flex-none items-center gap-1 rounded-full bg-[#fff7ea] px-2 py-0.5 text-[12px] font-bold text-[#a5762e]">
                  <Sparkles className="h-3 w-3" />+{c.reward}
                </span>
              </div>
              <p className="mt-1 flex-1 text-[13px] text-[#8a8278]">{c.desc}</p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#efe7df]">
                <div className="h-full rounded-full kk-coral-gradient transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-1 text-[12px] font-semibold text-[#a89f98]">{Math.min(prog, c.target)}/{c.target}</div>
              <button
                onClick={() => claim(c)}
                disabled={!done || claimed}
                data-testid={`claim-${c.id}`}
                className={`mt-3 rounded-full py-2.5 text-[13px] font-bold transition-colors ${
                  claimed ? "bg-[#e6f0e8] text-[#4e8a5f]" : done ? "kk-coral-gradient text-white" : "cursor-not-allowed bg-[#f1ece6] text-[#b3aaa4]"
                }`}
              >
                {claimed ? "Sudah diklaim ✓" : done ? "Klaim bonus" : "Belum selesai"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Misi spesial hari besar */}
      <div className="mb-4 mt-8 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight">
          <PartyPopper className="h-6 w-6 text-[#e0484d]" /> Misi spesial hari besar
        </h2>
        <span className="text-[12px] font-semibold text-[#8a8278]">Aksesori Mochi eksklusif</span>
      </div>
      {activeHoliday && (() => {
        const h = HOLIDAYS.find((x) => x.id === activeHoliday);
        if (!h) return null;
        return (
          <div data-testid="holiday-active-banner"
            className="mb-4 flex items-center gap-3 rounded-3xl p-4 text-white kk-pop"
            style={{ background: `linear-gradient(135deg, ${h.accent}, ${h.accent}cc)` }}>
            <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-white/20 text-2xl">{h.emoji}</span>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-widest text-white/85">Misi sedang aktif</div>
              <div className="text-[15px] font-extrabold">Spesial {h.label} lagi berlangsung! 🎉</div>
              <p className="text-[12px] text-white/85">Selesaikan misinya sebelum berakhir untuk aksesori {h.reward.label} {h.reward.emoji}.</p>
            </div>
          </div>
        );
      })()}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {HOLIDAYS.map((h) => {
          const target = h.mission.target;
          const prog = Math.min(holidayDone, target);
          const done = holidayDone >= target;
          const claimed = (state.holidaysClaimed || []).includes(h.id);
          const active = activeHoliday === h.id;
          const pct = Math.min(100, (holidayDone / target) * 100);
          return (
            <div key={h.id} data-testid={`holiday-${h.id}`}
              className={`relative flex flex-col overflow-hidden rounded-3xl border bg-white p-5 ${done && !claimed ? "border-[#f0c9cf]" : "border-[#efeae4]"}`}>
              {active && (
                <span className="absolute right-0 top-0 rounded-bl-2xl px-3 py-1 text-[10px] font-extrabold uppercase tracking-wide text-white" style={{ background: h.accent }} data-testid={`holiday-active-${h.id}`}>
                  Aktif sekarang
                </span>
              )}
              <div className="flex items-center gap-3">
                <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl text-2xl" style={{ background: `${h.accent}1a` }}>{h.emoji}</span>
                <div className="min-w-0">
                  <div className="truncate text-[15px] font-extrabold leading-tight">{h.label}</div>
                  <div className="truncate text-[12px] text-[#8a8278]">{h.tagline}</div>
                </div>
              </div>
              <p className="mt-3 flex-1 text-[13px] text-[#6e5f57]">{h.mission.desc}</p>
              <div className="mt-3 flex items-center gap-2 rounded-2xl bg-[#fff7f9] px-3 py-2">
                <span className="text-[20px]">{h.reward.emoji}</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-[#c9566b]">Hadiah eksklusif</div>
                  <div className="truncate text-[12px] font-bold text-[#6e594c]">{h.reward.label} · +{h.bonus} poin</div>
                </div>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#efe7df]">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: h.accent }} />
              </div>
              <div className="mt-1 text-[12px] font-semibold text-[#a89f98]">{prog}/{target} kencan selesai</div>
              <button
                onClick={() => claimH(h)}
                disabled={!done || claimed}
                data-testid={`claim-holiday-${h.id}`}
                className={`mt-3 flex items-center justify-center gap-1.5 rounded-full py-2.5 text-[13px] font-bold transition-colors ${
                  claimed ? "bg-[#e6f0e8] text-[#4e8a5f]" : done ? "text-white" : "cursor-not-allowed bg-[#f1ece6] text-[#b3aaa4]"
                }`}
                style={done && !claimed ? { background: h.accent } : {}}
              >
                {claimed ? <><Check className="h-4 w-4" /> Aksesori didapat</> : done ? <><Star className="h-4 w-4" /> Klaim aksesori</> : <><Lock className="h-3.5 w-3.5" /> Selesaikan misi</>}
              </button>
            </div>
          );
        })}
      </div>

      {/* redeem cards */}
      <h2 className="mb-4 mt-8 text-2xl font-extrabold tracking-tight">Tukar poin</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {REWARDS.map((r) => {
          const affordable = state.points >= r.cost;
          return (
            <div key={r.id} className="flex flex-col rounded-3xl border border-[#efeae4] bg-white p-5" data-testid={`reward-${r.id}`}>
              <div className="flex items-center justify-between">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-[#fff0ea] text-[#ff6b4a]"><Gift className="h-5 w-5" /></span>
                <span className="rounded-full bg-[#f6f2ed] px-2.5 py-1 text-[11px] font-semibold text-[#9b8e86]">min {rupiah(r.minSpend)}</span>
              </div>
              <div className="mt-4 text-3xl font-extrabold tracking-tight">{rupiah(r.value)}</div>
              <p className="mt-1 flex-1 text-[13px] text-[#8a8278]">{r.desc}</p>
              <div className="mt-4 flex items-center justify-between border-t border-dashed border-[#e7ddd7] pt-4">
                <span className="flex items-center gap-1.5 text-[13px] font-bold text-[#8a6b3d]">
                  <Coins className="h-4 w-4 text-[#e0a94b]" /> {r.cost} poin
                </span>
                <button
                  onClick={() => redeem(r)}
                  disabled={!affordable}
                  data-testid={`redeem-${r.id}`}
                  className={`rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${
                    affordable ? "kk-coral-gradient text-white" : "cursor-not-allowed bg-[#f1ece6] text-[#b3aaa4]"
                  }`}
                >
                  {affordable ? "Tukar" : "Kurang poin"}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* owned vouchers */}
      {activeVouchers.length > 0 && (
        <>
          <h2 className="mb-4 mt-8 text-2xl font-extrabold tracking-tight">Voucher kalian</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {activeVouchers.map((v) => (
              <div key={v.id} className="flex items-center gap-4 rounded-2xl border border-dashed border-[#d7b6a3] bg-[#fdf6ef] p-4" data-testid={`owned-voucher-${v.id}`}>
                <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-white text-[#be8c6f]"><Ticket className="h-5 w-5" /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-bold">{v.label}</div>
                  <div className="text-[12px] text-[#a78c7c]">Min. transaksi {rupiah(v.minSpend)} · pakai saat reservasi</div>
                </div>
                <span className="rounded-full bg-[#e6f0e8] px-2.5 py-1 text-[11px] font-bold text-[#4e8a5f]">Aktif</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* transactions */}
      <h2 className="mb-4 mt-8 text-2xl font-extrabold tracking-tight">Riwayat poin</h2>
      <div className="rounded-3xl border border-[#efeae4] bg-white p-2">
        {state.transactions.length === 0 ? (
          <p className="p-6 text-center text-[14px] text-[#8a8278]">Belum ada transaksi.</p>
        ) : (
          <ul className="divide-y divide-[#f2ede8]">
            {state.transactions.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-3 py-3.5" data-testid={`tx-${t.id}`}>
                <span className={`grid h-9 w-9 flex-none place-items-center rounded-full ${t.type === "credit" ? "bg-[#e6f0e8] text-[#4e8a5f]" : "bg-[#fdecec] text-[#c9363b]"}`}>
                  {t.type === "credit" ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-semibold text-[#1e1b18]">{t.label}</div>
                  <div className="text-[12px] text-[#a89f98]">{timeAgo(t.at)}</div>
                </div>
                <span className={`text-[14px] font-bold ${t.type === "credit" ? "text-[#4e8a5f]" : "text-[#c9363b]"}`}>
                  {t.type === "credit" ? "+" : "−"}{t.amount}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
